import { cn } from '@/ui/lib/utils'

export interface QuantLiveFigureCell {
  key: string
  label: string
  value: string
  tone?: 'pos' | 'neg' | 'warn'
  /** Full text on hover. */
  title?: string
  /** A small muted note on the value line (for example the time of the last trade). */
  sub?: string
}

export interface QuantLiveFiguresProps {
  cells: QuantLiveFigureCell[]
  /** The caption row; every money figure is labelled paper here. */
  caption: string
  /** Dims the panel when the state is old. */
  stale: boolean
  /** The "Figures are old" line, or `null`; its 16 px is reserved either way. */
  ageLine: string | null
}

const TONE: Record<NonNullable<QuantLiveFigureCell['tone']>, string> = {
  pos: 'text-accent-line',
  neg: 'text-alert',
  warn: 'text-[#CB8A00]',
}

/**
 * The figures of a live session (spec 041): a 24 px caption, four columns of cells of
 * exactly 44 px each (three rows for the twelve figures), and a reserved 16 px age line.
 * Server values only decide the text, never the size: each cell truncates with the full
 * text in `title`. Presentational: the caller builds the cells and the copy.
 */
export function QuantLiveFigures({ cells, caption, stale, ageLine }: QuantLiveFiguresProps) {
  return (
    <div
      role="group"
      aria-label="Session figures"
      data-live-figures=""
      data-stale={stale ? 'true' : 'false'}
      className="font-mono"
    >
      <div
        data-figures-caption=""
        className="flex h-6 items-center truncate text-[10px] tracking-[0.04em] text-paper-50"
        title={caption}
      >
        <span className="truncate">{caption}</span>
      </div>
      <div
        className={cn('grid grid-cols-4 border-t border-l border-paper-14', stale && 'opacity-50')}
      >
        {cells.map((cell) => {
          const text = cell.sub === undefined ? cell.value : `${cell.value} ${cell.sub}`
          return (
            <div
              key={cell.key}
              data-figure={cell.key}
              title={cell.title ?? text}
              className="box-border flex h-[44px] min-w-0 flex-col justify-center gap-0.5 overflow-hidden border-r border-b border-paper-14 px-3"
            >
              <span className="truncate text-[9px] leading-[12px] tracking-[0.14em] text-paper-40 uppercase">
                {cell.label}
              </span>
              <span
                className={cn(
                  'truncate text-[12px] leading-[16px]',
                  cell.tone ? TONE[cell.tone] : 'text-paper-90',
                )}
              >
                {cell.value}
                {cell.sub !== undefined && (
                  <span className="ml-2 text-[10px] text-paper-40">{cell.sub}</span>
                )}
              </span>
            </div>
          )
        })}
      </div>
      <div
        data-age-line=""
        className="h-4 truncate text-[10px] leading-4 text-paper-50"
        title={ageLine ?? undefined}
      >
        {ageLine}
      </div>
    </div>
  )
}
